import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import GoogleAuth from './components/GoogleAuth';
import CompetitorSelector from './components/CompetitorSelector';
import CompetitorManager from './components/CompetitorManager';
import BeeSelector from './components/BeeSelector';
import SpellingPractice from './components/SpellingPractice';
import CompetitorPractice from './components/CompetitorPractice';
import CompetitorAnalysis from './components/CompetitorAnalysis';
import AdminPanel from './components/AdminPanel';
import ApiService from './services/api';
import './index.css';

const AppContent = () => {
  const { user, signIn, signOut, loading } = useAuth();

  // Initialize state with defaults (will be restored from localStorage after auth loads)
  const [currentView, setCurrentView] = useState('competitorSelection');
  const [selectedCompetitor, setSelectedCompetitor] = useState(null);
  const [selectedBee, setSelectedBee] = useState(null);
  const [stateRestored, setStateRestored] = useState(false);

  // Restore state from localStorage after user authentication is complete
  useEffect(() => {
    if (!loading && user && !stateRestored) {
      // Only restore state once when authentication is complete
      const savedView = localStorage.getItem('spellingBeeCurrentView');
      const savedCompetitor = localStorage.getItem('spellingBeeSelectedCompetitor');
      const savedBee = localStorage.getItem('spellingBeeSelectedBee');

      if (savedView) {
        setCurrentView(savedView);
      }
      if (savedCompetitor) {
        try {
          setSelectedCompetitor(JSON.parse(savedCompetitor));
        } catch (e) {
          localStorage.removeItem('spellingBeeSelectedCompetitor');
        }
      }
      if (savedBee) {
        try {
          setSelectedBee(JSON.parse(savedBee));
        } catch (e) {
          localStorage.removeItem('spellingBeeSelectedBee');
        }
      }

      setStateRestored(true);
    }
  }, [loading, user, stateRestored]);

  // Save state to localStorage whenever it changes (only after initial restoration)
  useEffect(() => {
    if (user && stateRestored) {
      localStorage.setItem('spellingBeeCurrentView', currentView);
    }
  }, [currentView, user, stateRestored]);

  useEffect(() => {
    if (user && stateRestored) {
      if (selectedCompetitor) {
        localStorage.setItem('spellingBeeSelectedCompetitor', JSON.stringify(selectedCompetitor));
      } else {
        localStorage.removeItem('spellingBeeSelectedCompetitor');
      }
    }
  }, [selectedCompetitor, user, stateRestored]);

  useEffect(() => {
    if (user && stateRestored) {
      if (selectedBee) {
        localStorage.setItem('spellingBeeSelectedBee', JSON.stringify(selectedBee));
      } else {
        localStorage.removeItem('spellingBeeSelectedBee');
      }
    }
  }, [selectedBee, user, stateRestored]);

  // Clear state when user signs out
  useEffect(() => {
    if (!loading && !user) {
      localStorage.removeItem('spellingBeeCurrentView');
      localStorage.removeItem('spellingBeeSelectedCompetitor');
      localStorage.removeItem('spellingBeeSelectedBee');
      setCurrentView('competitorSelection');
      setSelectedCompetitor(null);
      setSelectedBee(null);
      setStateRestored(false);
    }
  }, [loading, user]);
  
  const ADMIN_EMAILS = ['md.wgtn@gmail.com'];
  const isAdmin = user && ADMIN_EMAILS.includes(user.email);

  const handleCompetitorSelected = async (competitor) => {
    console.log('Selected competitor:', competitor);
    console.log('Competitor current_bee_id:', competitor.current_bee_id);
    setSelectedCompetitor(competitor);

    // If competitor has a current bee, try to navigate directly to it
    if (competitor.current_bee_id) {
      try {
        // Get bee details to set the selected bee
        const response = await ApiService.getAllBees();
        if (response.success) {
          const currentBee = response.bees.find(bee => bee.bee_id === competitor.current_bee_id);

          if (currentBee) {
            setSelectedBee(currentBee);
            setCurrentView('practice');
            return;
          }
        }
      } catch (error) {
        console.error('Error fetching current bee:', error);
      }
    }

    // Fallback to bee selection if no current bee or error occurred
    setCurrentView('beeSelection');
  };

  const handleManageCompetitors = () => {
    setCurrentView('competitorManager');
  };

  const handleBackToCompetitorSelection = () => {
    setCurrentView('competitorSelection');
    setSelectedCompetitor(null);
    setSelectedBee(null);
  };

  const handleCompetitorCreated = (competitor) => {
    // Optionally auto-select the newly created competitor
    setSelectedCompetitor(competitor);
    setCurrentView('beeSelection');
  };

  const handleBeeSelected = async (bee) => {
    console.log('Bee selected:', bee);
    console.log('Selected competitor:', selectedCompetitor);
    console.log('Current user:', user);
    setSelectedBee(bee);
    setCurrentView('practice');

    // Update the competitor's current bee
    if (selectedCompetitor && user) {
      try {
        console.log('Calling updateCompetitorCurrentBee API...');
        const result = await ApiService.updateCompetitorCurrentBee(
          selectedCompetitor.competitor_id,
          bee.bee_id,
          user.user_id
        );
        console.log('API result:', result);

        // Update the selected competitor state to reflect the new current bee
        setSelectedCompetitor(prev => ({
          ...prev,
          current_bee_id: bee.bee_id
        }));
        console.log('Updated competitor state with current_bee_id:', bee.bee_id);
      } catch (error) {
        console.error('Error updating competitor current bee:', error);
        // Continue anyway since the bee selection still works
      }
    }
  };

  const handleBackToBeeSelection = () => {
    setCurrentView('beeSelection');
    setSelectedBee(null);
  };

  const handleShowAdmin = () => {
    setCurrentView('admin');
  };

  const handleBackFromAdmin = () => {
    setCurrentView('competitorSelection');
  };

  const handleShowCompetitorAnalysis = (competitor) => {
    setSelectedCompetitor(competitor);
    setCurrentView('competitorAnalysis');
  };

  const handleBackFromAnalysis = () => {
    setCurrentView('competitorSelection');
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container">
        <GoogleAuth onSignIn={signIn} onSignOut={signOut} user={user} />
      </div>
    );
  }

  // If user is a competitor, show the competitor practice interface
  if (user.user_type === 'competitor') {
    return (
      <div className="container" style={{ paddingBottom: '100px' }}>
        <CompetitorPractice
          onBack={() => signOut()}
        />

        {/* Bottom controls for competitor */}
        <div style={{
          position: 'fixed',
          bottom: '0',
          left: '0',
          right: '0',
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #e2e8f0',
          padding: '15px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <GoogleAuth onSignIn={signIn} onSignOut={signOut} user={user} />
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingBottom: '100px' }}>
      
      {currentView === 'competitorSelection' && (
        <CompetitorSelector
          onCompetitorSelected={handleCompetitorSelected}
          onManageCompetitors={handleManageCompetitors}
          onShowAnalysis={handleShowCompetitorAnalysis}
        />
      )}
      
      {currentView === 'competitorManager' && (
        <CompetitorManager 
          onBack={handleBackToCompetitorSelection}
          onCompetitorCreated={handleCompetitorCreated}
        />
      )}
      
      {currentView === 'beeSelection' && selectedCompetitor && (
        <BeeSelector 
          onBeeSelected={handleBeeSelected}
          selectedCompetitor={selectedCompetitor}
        />
      )}
      
      {currentView === 'practice' && selectedBee && selectedCompetitor && (
        <SpellingPractice 
          selectedBee={selectedBee} 
          selectedCompetitor={selectedCompetitor}
          onBackToBeeSelection={handleBackToBeeSelection}
        />
      )}
      
      {currentView === 'competitorAnalysis' && selectedCompetitor && (
        <CompetitorAnalysis
          competitor={selectedCompetitor}
          onBack={handleBackFromAnalysis}
        />
      )}

      {currentView === 'admin' && (
        <>
          <AdminPanel />
          <div style={{ textAlign: 'center', margin: '20px 0' }}>
            <button
              onClick={handleBackFromAdmin}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Back to Competitor Selection
            </button>
          </div>
        </>
      )}

      {/* Bottom controls section */}
      <div style={{
        position: 'fixed',
        bottom: '0',
        left: '0',
        right: '0',
        backgroundColor: '#f8f9fa',
        borderTop: '1px solid #e2e8f0',
        padding: '15px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '15px',
        flexWrap: 'wrap'
      }}>
        {/* Change Competitor button */}
        {selectedCompetitor && currentView !== 'competitorSelection' && currentView !== 'competitorManager' && (
          <button
            onClick={handleBackToCompetitorSelection}
            style={{
              padding: '8px 16px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Change Competitor
          </button>
        )}

        {/* Change Bee button */}
        {currentView === 'practice' && selectedBee && (
          <button
            onClick={handleBackToBeeSelection}
            style={{
              padding: '8px 16px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Change Bee
          </button>
        )}

        {/* Admin Panel button */}
        {isAdmin && currentView !== 'admin' && (
          <button
            onClick={handleShowAdmin}
            style={{
              padding: '8px 16px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Admin Panel
          </button>
        )}

        {/* User info and sign out */}
        <GoogleAuth onSignIn={signIn} onSignOut={signOut} user={user} />
      </div>
    </div>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;